import React, { useState } from "react";
import { Box, Typography, CardMedia, Button } from "@material-ui/core";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exerciseEditPath } from "routes/createContent/paths";
import Video from "./Video";
import DeletedExerciseWarning from "./DeletedExerciseWarning";
import { SmartphonePreview } from "./SmartphonePreview";
import { Title } from "./styles";
import { ImageAndVideoContainer } from "./SmartphonePreview/ImageAndVideoContainer";
import { AboutContainer, Divider, InstructionCard, StyledBody, StyledH4 } from "./SmartphonePreview/styles";
import { IconText } from "./SmartphonePreview/IconText";
import { Info as FaInfo, RotateRight as FaRotateRight, Timer as FaTimer } from "@material-ui/icons";
import { TipCard } from "./SmartphonePreview/TipCard";

const ExercisePreview: React.FC<{ exercise: any }> = ({ exercise }) => {
  const { t } = useTranslation();
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [selectedLocale, setSelectedLocale] = useState<string>(exercise.levels[0].translations[0].locale);

  const {
    creator_id,
    duration_minutes,
    exercise_number,
    name,
    premium,
    primary_function,
    state,
    target_joints,
    updated_at,
    visibility,
  } = exercise;

  const level = exercise.levels[selectedLevel] as any;

  const { reps, duration_icon, duration_unit, equipment, level: levelNumber, movement, position, sets } = level;

  const translation = level.translations.find((t: any) => t.locale === selectedLocale);
  const {
    title,
    image_url,
    video_file_url,
    video_thumbnail_file_url,
    caption_file_url,
    about,
    instructions_text1,
    instructions_text2,
    instructions_text3,
    instructions_icon_name,
    tip_description,
    tip_title,
    locale,
    variation_title,
  } = translation;

  const isTimedExercise = instructions_icon_name === "timer";

  const details = {
    creator_id: creator_id != null ? t("creator.pt_label", { id: creator_id }) : t("creator.joint_academy"),
    duration_minutes,
    exercise_number,
    name,
    premium,
    primary_function,
    state,
    target_joints,
    updated_at,
    visibility,
    reps,
    duration_icon,
    duration_unit,
    equipment,
    level: levelNumber,
    movement,
    position,
    sets,
    title,
    image_url,
    video_file_url,
    video_thumbnail_file_url,
    caption_file_url,
    about,
    instructions_text1,
    instructions_text2,
    instructions_text3,
    instructions_icon_name,
    tip_description,
    tip_title,
    locale,
    variation_title,
  };

  return (
    <Box p={2}>
      {exercise.deleted_at && <DeletedExerciseWarning />}
      <Typography variant="h6">Select Level:</Typography>
      <Box display="flex" flexDirection="row" mb={2}>
        {exercise.levels.map((level: { name: string }, index: number) => (
          <Box key={index} mr={2} display="flex" alignItems="center">
            <input
              type="radio"
              id={`level-${index}`}
              name="level"
              value={index}
              checked={selectedLevel === index}
              onChange={() => {
                setSelectedLevel(index);
                setSelectedLocale(exercise.levels[index].translations[0].locale);
              }}
              style={{ marginRight: 6 }}
            />
            <label htmlFor={`level-${index}`}>{`Level ${index + 1}`}</label>
          </Box>
        ))}
      </Box>
      <Typography variant="h6">Select Locale:</Typography>
      <Box display="flex" flexDirection="row" mb={2}>
        {level.translations.map((translation: any, tIndex: number) => (
          <Box key={tIndex} mr={2} display="flex" alignItems="center">
            <input
              type="radio"
              id={`locale-${translation.locale}`}
              name="locale"
              value={translation.locale}
              checked={selectedLocale === translation.locale}
              onChange={() => setSelectedLocale(translation.locale)}
              style={{ marginRight: 6 }}
            />
            <label htmlFor={`locale-${translation.locale}`}>{translation.locale}</label>
          </Box>
        ))}
      </Box>
      <hr style={{ margin: "24px 0" }} />
      <Box display="flex" justifyContent="center">
        <SmartphonePreview>
          <Title>{title}</Title>

          <ImageAndVideoContainer
            displayMediaTabs={false}
            isLoading={false} // TODO: Fix
            image_url={image_url || ""}
            caption_file_url={caption_file_url || undefined}
            video_file_url={video_file_url || ""}
            video_thumbnail_file_url={video_thumbnail_file_url || ""}
            previewTab={video_file_url ? "video" : "image"} // TODO: Fix
          />
          <InstructionCard>
            {(instructions_text1 || instructions_text3) && (
              <>
                <IconText icon={<FaInfo />} text={`${instructions_text1 || ""} ${instructions_text3 || ""}`} />
                <Divider />
              </>
            )}
            <>
              {isTimedExercise && <IconText icon={<FaTimer />} text={`${reps} ${instructions_text2 || ""}`} />}
              {!isTimedExercise && <IconText icon={<FaRotateRight />} text={`${reps} ${instructions_text2 || ""}`} />}
              <Divider />
            </>

            {sets && sets > 1 && (
              <>
                <IconText icon={<>#</>} text={t("view_exercise.sets", { sets })} />
              </>
            )}

            {tip_description && tip_title && <TipCard tip_description={tip_description} tip_title={tip_title} />}
          </InstructionCard>

          {about && (
            <AboutContainer>
              <StyledH4>About</StyledH4>
              <StyledBody>{about}</StyledBody>
            </AboutContainer>
          )}
        </SmartphonePreview>
      </Box>

      <hr style={{ margin: "24px 0" }} />
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box flex="1" maxWidth="50%" pr={1}>
          {translation.image_url && (
            <Box>
              <Typography variant="h6">Image:</Typography>
              <CardMedia
                component="img"
                image={translation.image_url}
                alt="Exercise Image"
                style={{ maxHeight: 320, maxWidth: "100%" }}
              />
            </Box>
          )}
        </Box>
        <Box flex="1" maxWidth="50%" pl={1}>
          {translation.video_file_url && (
            <Box>
              <Typography variant="h6">Video:</Typography>
              <div style={{ maxHeight: 320, maxWidth: "100%" }}>
                <Video
                  videoFileUrl={translation.video_file_url}
                  poster={translation.video_thumbnail_file_url}
                  captionFileUrl={translation.caption_file_url}
                />
              </div>
            </Box>
          )}
        </Box>
      </Box>

      <hr style={{ margin: "24px 0" }} />
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
        <tbody>
          {Object.entries(details).map(([key, value], index) => {
            const displayValue = Array.isArray(value) ? value.join(", ") : String(value);

            return (
              <tr
                key={key}
                style={{
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: index % 2 === 0 ? "#f9f9f9" : "transparent",
                }}
              >
                <td style={{ padding: "12px 8px", verticalAlign: "top", width: "30%" }}>
                  <Typography style={{ fontWeight: "bold" }}>{key}</Typography>
                </td>
                <td style={{ padding: "12px 8px", verticalAlign: "top" }}>
                  <Typography>{displayValue}</Typography>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Typography variant="h6">Full json</Typography>
      <textarea style={{ width: "100%", height: 500 }} readOnly value={JSON.stringify(exercise, null, 2)} />
      <Box mb={2} mt={2}>
        <Button variant="contained" color="primary" component={Link} to={exerciseEditPath(exercise.id)}>
          Edit
        </Button>
      </Box>
    </Box>
  );
};

export default ExercisePreview;
